import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CurrencySelect } from '@/components/CurrencySelect';
import { User, Mail, DollarSign, Edit, Save, X } from 'lucide-react';
import { usersService } from '@/services/supabase';
import LoadingAnimation from '@/components/LoadingAnimation';
import { BusinessCard } from '@/components/BusinessCard';

export const ProfilePage = (): React.ReactNode => {
  const { supabaseUser, firebaseUser, loading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    display_name: '',
    preferred_currency: '',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingAnimation message="Loading profile..." size="md" />
      </div>
    );
  }

  if (!supabaseUser || !firebaseUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">No user data available</p>
      </div>
    );
  }

  const handleEdit = () => {
    setEditForm({
      display_name: supabaseUser.display_name || '',
      preferred_currency: supabaseUser.preferred_currency || 'USD',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      display_name: '',
      preferred_currency: '',
    });
  };

  const handleSave = async () => {
    if (!firebaseUser) return;

    setIsSaving(true);
    try {
      await usersService.updateUser(firebaseUser.uid, {
        display_name: editForm.display_name || null,
        preferred_currency: editForm.preferred_currency || 'USD',
      });

      // Refresh the page to get updated data
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Profile</h1>
        {!isEditing && (
          <Button onClick={handleEdit} variant="outline" className="gap-2">
            <Edit className="h-4 w-4" />
            Edit Profile
          </Button>
        )}
      </div>

      <Card
        className={!isEditing ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""}
        onClick={() => !isEditing && handleEdit()}
      >
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Profile Photo */}
          <div className="flex items-center gap-6">
            <div className="relative">
              {supabaseUser.photo_url ? (
                <img
                  src={supabaseUser.photo_url}
                  alt={supabaseUser.display_name || 'User'}
                  className="h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
                  <span className="text-3xl font-semibold text-primary">
                    {(supabaseUser.display_name || supabaseUser.email || 'U')
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    value={editForm.display_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, display_name: e.target.value })
                    }
                    placeholder="Enter your name"
                  />
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold">
                    {supabaseUser.display_name || 'No name set'}
                  </h2>
                  <p className="text-muted-foreground">User Profile</p>
                </>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address
            </Label>
            <Input value={supabaseUser.email} disabled className="bg-gray-50" />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed
            </p>
          </div>

          {/* Preferred Currency */}
          <div className="space-y-2">
            <Label htmlFor="preferred_currency" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Preferred Currency
            </Label>
            {isEditing ? (
              <CurrencySelect
                value={editForm.preferred_currency}
                onValueChange={(value) =>
                  setEditForm({ ...editForm, preferred_currency: value })
                }
              />
            ) : (
              <Input
                value={supabaseUser.preferred_currency || 'USD'}
                disabled
                className="bg-gray-50"
              />
            )}
          </div>

          {/* User ID (readonly) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              User ID
            </Label>
            <Input value={supabaseUser.id} disabled className="bg-gray-50 font-mono text-xs" />
            <p className="text-xs text-muted-foreground">
              Your unique user identifier
            </p>
          </div>

          {/* Action Buttons (shown when editing) */}
          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                disabled={isSaving}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Business Information */}
      <BusinessCard />
    </div>
  );
};

export default ProfilePage;
